<template>
	<AppLayout :title="trans('admin.periods.events.title')">
		<Modal id="create-period-modal">
			<template #title>{{ trans('admin.periods.events.title') }}</template>
			<div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
				<LabeledObject required>
					<template #label>{{ trans('admin.periods.events.edition') }}</template>
					<input v-model="form.edition" type="text" maxlength="255">
				</LabeledObject>
				<LabeledObject required>
					<template #label>{{ trans('admin.periods.events.year') }}</template>
					<input v-model="form.year" type="number">
				</LabeledObject>
				<LabeledObject required>
					<template #label>{{ trans('admin.periods.events.begins_on') }}</template>
					<IconDateInput v-model="form.begins_on" icon-class="fa-solid fa-hourglass-start" :min="min" full/>
				</LabeledObject>
				<LabeledObject required>
					<template #label>{{ trans('admin.periods.events.ends_on') }}</template>
					<IconDateInput v-model="form.ends_on" icon-class="fa-solid fa-hourglass-end" :min="form.begins_on" full/>
				</LabeledObject>
				<LabeledObject required>
					<template #label>{{ trans('admin.periods.events.semester') }}</template>
					<select v-model="form.semester">
						<option v-for="semester in Semester" :value="semester">{{ trans(`events.semester.${semester}`) }}</option>
					</select>
				</LabeledObject>
			</div>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="create">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
		</Modal>
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="showModal('create-period-modal')">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</PrimaryButton>
		</template>
		<Pagination :resource="periods"/>
		<Empty v-if="periods.data.length === 0"/>
		<CardGrid v-else>
			<Card v-for="period in periods.data" :key="`periods-${period.id}`" :title="`${dayjs(period.begins_on).format('DD MMM YY')} - ${dayjs(period.ends_on).format('DD MMM YY')}`">
				<div class="flex items-start gap-3">
					<LabeledObject>
						<template #label>{{ trans('admin.periods.events.edition') }}</template>
						{{ period.edition }}
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('admin.periods.events.year') }}</template>
						{{ period.year }}
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('admin.periods.events.semester') }}</template>
						{{ trans(`events.semester.${period.semester}`) }}
					</LabeledObject>
				</div>
				<template #footer>
					<Link :as="SecondaryButton" :color="Color.Blue" :href="route('events.periods.edit', period.id)">
						<i class="fa-solid fa-edit mr-2"/>
						{{ trans('general.edit') }}
					</Link>
				</template>
			</Card>
		</CardGrid>
		<Pagination :resource="periods"/>
	</AppLayout>
</template>
<script setup lang="ts">
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import Card from '@/Components/UI/Cards/Card.vue'
import {defineProps, type Ref, ref} from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import {Semester} from '@/Types/Enums/Semester'
import Modal from '@/Components/UI/Modals/Modal.vue'
import Pagination from '@/Components/UI/Pagination.vue'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {type InertiaForm, Link, useForm} from '@inertiajs/vue3'
import IconDateInput from '@/Components/UI/Inputs/IconDateInput.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {type PaginatedModel} from '@/Types/Pagination/PaginatedModel'
import {type EventPeriod} from '@/Types/Periods/Events/EventPeriod'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	periods:PaginatedModel<EventPeriod>,
}>()

const min:Ref<string> = ref(dayjs().format('YYYY-MM-DD'))
const working:Ref<boolean> = ref(false)
const periods:Ref<PaginatedModel<EventPeriod>> = ref(props.periods)
const form:InertiaForm<EventPeriod> = useForm<EventPeriod>({
	begins_on: dayjs().format('YYYY-MM-DD'),
	ends_on: dayjs().format('YYYY-MM-DD'),
	edition: '',
	year: dayjs().year(),
	semester: Semester.Spring,
} as EventPeriod)

function create():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.inserting'))
		form.post(route('events.periods.store'), {
			onSuccess: () => {
				showSuccess(trans('general.inserted'))
			},
			onError: showValidationErrors,
			onFinish: () => {
				working.value = false
			},
		})
	}
}
</script>